import { Link } from 'react-router-dom';
import { useTrip } from '../lib/tripContext.jsx';
import { useUser } from '../lib/userContext.jsx';
import Countdown from '../components/Countdown.jsx';
import FlightCard from '../components/FlightCard.jsx';
import { formatDateShort, formatTime } from '../lib/format.js';

export default function TripHub() {
  const { trip } = useTrip();
  const { player } = useUser();

  if (!trip) return null;

  const firstOutbound = player?.flight_outbound?.[0];
  const tripStartIso = firstOutbound?.depart || trip.dates.start;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-6 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          {trip.dates.start} → {trip.dates.end}
        </p>
        <h1 className="mt-1 font-serif text-2xl">{trip.name}</h1>
        {trip.tagline && (
          <p className="mt-1 italic text-fg-secondary">&ldquo;{trip.tagline}&rdquo;</p>
        )}
      </header>

      <Countdown
        targetIso={tripStartIso}
        label={player ? `Until ${player.display_name || player.name}'s departure` : 'Until departure'}
      />

      <Section title="Your flights" subtitle={player?.display_name || player?.name}>
        <div className="space-y-3">
          <SubSection label="Outbound → Juneau">
            {(player?.flight_outbound || []).map((leg, i) => (
              <FlightCard key={`${leg.number}-${i}`} leg={leg} />
            ))}
          </SubSection>
          <SubSection label="Return ← Home">
            {(player?.flight_return || []).map((leg, i) => (
              <FlightCard key={`${leg.number}-${i}`} leg={leg} />
            ))}
          </SubSection>
        </div>
      </Section>

      <Section title="Lodging">
        <LodgingCard lodging={trip.lodging} timezone={trip.dates.timezone} />
      </Section>

      <Section title="The crew">
        <PlayerList trip={trip} />
      </Section>

      <Section title="Quick actions">
        <div className="grid grid-cols-3 gap-2">
          <QuickLink to="/today" label="Today" />
          <QuickLink to="/wilman" label="Wilman" />
          <QuickLink to="/sos" label="SOS" tone="danger" />
        </div>
      </Section>

      <footer className="pt-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
        Built by Kishan S Patel
      </footer>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="font-mono text-xs uppercase tracking-wider text-fg-secondary">{title}</h2>
        {subtitle && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {subtitle}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function SubSection({ label, children }) {
  return (
    <div className="space-y-1.5">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fg-tertiary">{label}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function LodgingCard({ lodging, timezone }) {
  if (!lodging) return null;
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    lodging.address
  )}`;
  return (
    <div className="card divide-y divide-border-tertiary">
      <div className="px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {lodging.type} · hosted by {lodging.host}
        </p>
        <p className="mt-1 font-medium">{lodging.address}</p>
        {lodging.confirmation_code && (
          <p className="mt-1 font-mono text-xs text-fg-secondary">
            Confirmation · {lodging.confirmation_code}
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 divide-x divide-border-tertiary text-sm">
        <CheckTime label="Check-in" iso={lodging.checkin} timezone={timezone} />
        <CheckTime label="Check-out" iso={lodging.checkout} timezone={timezone} />
      </div>
      <a
        href={directions}
        target="_blank"
        rel="noopener noreferrer"
        className="block px-4 py-2.5 text-center font-mono text-xs uppercase tracking-wider text-action transition hover:bg-bg-tertiary"
      >
        Open in Maps
      </a>
    </div>
  );
}

function CheckTime({ label, iso, timezone }) {
  return (
    <div className="px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">{label}</p>
      <p className="mt-0.5 font-medium">{formatDateShort(iso, { timezone })}</p>
      <p className="text-xs text-fg-secondary">{formatTime(iso, { timezone })}</p>
    </div>
  );
}

function PlayerList({ trip }) {
  return (
    <ul className="card divide-y divide-border-tertiary">
      {trip.players.map((p) => (
        <li key={p.id} className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="font-medium">{p.display_name || p.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {p.wilman_persona || 'player'}
            </p>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {p.home_address}
          </p>
        </li>
      ))}
      {trip.spectators?.map((s) => (
        <li key={s.id} className="flex items-center justify-between bg-bg-tertiary px-4 py-3">
          <div>
            <p className="font-medium">{s.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {s.title || s.role}
            </p>
          </div>
          <span className="rounded-full border border-copper/40 bg-copper/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            spectator
          </span>
        </li>
      ))}
    </ul>
  );
}

function QuickLink({ to, label, tone }) {
  const danger = tone === 'danger';
  return (
    <Link
      to={to}
      className={[
        'flex h-12 items-center justify-center rounded-lg border font-mono text-xs uppercase tracking-wider transition',
        danger
          ? 'border-danger/40 bg-danger/10 text-danger hover:bg-danger/15'
          : 'border-border-tertiary bg-bg-secondary text-fg-primary hover:bg-bg-tertiary',
      ].join(' ')}
    >
      {label}
    </Link>
  );
}
